import React, { useCallback, useState } from 'react'
import { InvestorBankDetails } from '../../lib/types'
import { copyTextToClipboard } from '../../lib/utils/copyToClipboard'
import './invest-v2-bank-cards.scss'

const BankIcon = () => (
  <svg
    className='iv2-bank-card__icon'
    width='22'
    height='22'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    aria-hidden
  >
    <path
      d='M3 10.5L12 4l9 6.5V20a1 1 0 01-1 1H4a1 1 0 01-1-1v-9.5z'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinejoin='round'
    />
    <path d='M9 21V12h6v9' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' />
  </svg>
)

const CopyIcon = () => (
  <svg width='18' height='18' viewBox='0 0 24 24' fill='none' aria-hidden>
    <path
      d='M8 7V5a2 2 0 012-2h8a2 2 0 012 2v10a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2M8 7h8a2 2 0 012 2v2'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
    />
  </svg>
)

const FIELDS: { key: keyof InvestorBankDetails; label: string }[] = [
  { key: 'beneficiaryName', label: 'Beneficiary name' },
  { key: 'accountNumber', label: 'Beneficiary account number' },
  { key: 'beneficiaryBank', label: 'Beneficiary bank name' },
  { key: 'bankAddress', label: 'Beneficiary bank address' },
  { key: 'cif', label: 'Customer information file (CIF) number' },
  { key: 'swiftCode', label: 'SWIFT code' },
]

type Props = {
  bankDetails: InvestorBankDetails
}

const InvestV2InvestorBankCard = ({ bankDetails: details }: Props) => {
  const [copiedKey, setCopiedKey] = useState<string | null>(null)

  const flashCopied = useCallback((key: string) => {
    setCopiedKey(key)
    window.setTimeout(() => setCopiedKey(null), 1600)
  }, [])

  const onCopy = useCallback(
    async (key: string, text: string) => {
      const ok = await copyTextToClipboard(text)
      if (ok) flashCopied(key)
    },
    [flashCopied],
  )

  const emptyLabel = 'NA'
  const copyAllText = FIELDS.map(({ key, label }) => {
    const v = details[key]
    const s = String(v ?? '').trim() !== '' ? String(v) : emptyLabel
    return `${label}: ${s}`
  }).join('\n')

  return (
    <div
      className='iv2-bank-card iv2-bank-card--investor'
      data-testid='invest-v2-investor-bank-card'
    >
      <div className='iv2-bank-card__head'>
        <div className='iv2-bank-card__head-left'>
          <BankIcon />
          <h3 className='iv2-bank-card__title'>Your bank details</h3>
        </div>
        <button
          type='button'
          className='iv2-bank-card__copy-all'
          onClick={() => void onCopy('all', copyAllText)}
        >
          <CopyIcon />
          {copiedKey === 'all' ? 'Copied' : 'Copy all'}
        </button>
      </div>
      <div className='iv2-bank-card__body'>
        {FIELDS.map(({ key, label }) => {
          const raw = details[key]
          const value = String(raw ?? '').trim() !== '' ? String(raw) : emptyLabel
          return (
            <div className='iv2-bank-field' key={key}>
              <span className='iv2-bank-field__label'>{label}</span>
              <div className='iv2-bank-field__row'>
                <span className='iv2-bank-field__value'>{value}</span>
                {value !== emptyLabel ? (
                  <button
                    type='button'
                    className='iv2-bank-field__copy'
                    aria-label={`Copy ${label}`}
                    onClick={() => void onCopy(key, value)}
                  >
                    <CopyIcon />
                  </button>
                ) : null}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export { InvestV2InvestorBankCard }
